import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConstantsService } from './api-constants.service';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  Dashboard,
  DashboardCreationParams,
  DashboardListParams,
  Pagination,
  SuperSetDashboard,
  SuperSetDashboardParams,
  SuperSetTokenCreationParams
} from '~core/models/core.models';
import { PageName } from '~backoffice/app/~shared/models/shared.models';
import { ConstantsService } from './constants.service';
import { Store } from '@ngxs/store';
import { AuthState } from '~backoffice/app/~store/states/auth.state';
import { StatisticService } from './statistic.service';
import { SupersetResource } from '~core/models/dashboard.models';

@Injectable()
export class DashboardService {
  private _httpClient = inject(HttpClient);
  private _apiConstantsService = inject(ApiConstantsService);
  private _constantsService = inject(ConstantsService);
  private _store = inject(Store);
  private _statisticService = inject(StatisticService);

  get apiUrl(): string {
    return this._apiConstantsService.dashboardUrl;
  }

  read(dashboardId: string): Observable<Dashboard> {
    return this._httpClient.get<Dashboard>(`${this.apiUrl}/${dashboardId}`);
  }

  list(params?: DashboardListParams): Observable<Pagination<Dashboard>> {
    return this._httpClient.get<Pagination<Dashboard>>(this.apiUrl, {
      params: {
        ...params,
        sort: params?.sort || 'createdAt',
        desc: params?.desc || 'createdAt'
      } as Record<string, any>
    });
  }

  create(data: DashboardCreationParams): Observable<Dashboard> {
    return this._httpClient.post<Dashboard>(this.apiUrl, {
      ...data,
      useStateFormViolationHandler: true
    });
  }

  update(
    id: string,
    data: Partial<DashboardCreationParams>
  ): Observable<Dashboard> {
    return this._httpClient.patch<Dashboard>(`${this.apiUrl}/${id}`, {
      ...data,
      useStateFormViolationHandler: true
    });
  }

  delete(dashboardId: string): Observable<unknown> {
    return this._httpClient.delete<unknown>(`${this.apiUrl}/${dashboardId}`);
  }

  listSuperSetSources(
    params?: DashboardListParams
  ): Observable<Pagination<SuperSetDashboard>> {
    return this._httpClient
      .get<
        Pagination<SuperSetDashboard> & {
          dashboards: SuperSetDashboard[];
        }
      >(`${this._apiConstantsService.supersetUrl}/dashboards`, {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      })
      .pipe(map(res => ({ ...res, items: res.dashboards })));
  }

  listSuperSetDashboard(
    params?: DashboardListParams
  ): Observable<Pagination<SuperSetDashboard>> {
    return this._httpClient
      .get<
        Pagination<SuperSetDashboard> & {
          dashboards: SuperSetDashboard[];
        }
      >(`${this._apiConstantsService.supersetUrl}/dashboards`, {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt',
          skipJwtInterceptorErrors: true
        } as Record<string, any>
      })
      .pipe(map(res => ({ ...res, items: res.dashboards })));
  }

  listUserSuperSetDashboard(
    params?: DashboardListParams
  ): Observable<Pagination<SuperSetDashboard>> {
    return this._httpClient
      .get<
        Pagination<SuperSetDashboard> & {
          dashboards: SuperSetDashboard[];
        }
      >(`${this._apiConstantsService.supersetUrl}/user/dashboards`, {
        params: {
          ...params,
          sort: params?.sort || 'order',
          asc: params?.asc || 'order'
          // skipJwtInterceptorErrors: true
        } as Record<string, any>
      })
      .pipe(map(res => ({ ...res, items: res.dashboards })));
  }

  updateSuperSetDashboardVisibility(
    id: string,
    data: Pick<SuperSetDashboard, 'visibleInApp'>
  ): Observable<SuperSetDashboard> {
    return this._httpClient.put<SuperSetDashboard>(
      `${this._apiConstantsService.supersetUrl}/dashboards/${id}/visibility`,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  updateSuperSetDashboardOrder(
    id: string,
    data: Pick<SuperSetDashboard, 'order'>
  ): Observable<SuperSetDashboard> {
    return this._httpClient.put<SuperSetDashboard>(
      `${this._apiConstantsService.supersetUrl}/dashboards/${id}/order `,
      {
        ...data,
        useStateFormViolationHandler: true
      }
    );
  }

  getSuperSetGuestToken(
    params: SuperSetTokenCreationParams
  ): Observable<string> {
    return this._httpClient
      .post<{
        token: string;
      }>(`${this._apiConstantsService.supersetUrl}/auth/guest-token`, params)
      .pipe(map(res => res.token));
  }

  getSuperSetOAuthUrl(): Observable<string> {
    const accessTokenId = this._store.selectSnapshot(AuthState.accessToken)?.id;
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this._constantsService.environment.superSetClientId,
      redirect_uri: `${this._constantsService.environment.superSetDomainBasePathUrl}/oauth-authorized/dgi`,
      scope: 'openid profile email',
      state: Math.random().toString(36).substring(2, 15),
      bearer: accessTokenId || ''
    });
    return of(
      `${this._constantsService.environment.superSetDomainBasePathUrl}/login/dgi?bearer=${accessTokenId}`
    );
    // return of(
    //   `${this._constantsService.environment.apiUrl}/identity/oauth/authorize?${params}`
    // );
  }

  getEmbeddableSourceUuid(sourceId: string): Observable<string> {
    return this._httpClient
      .get<{
        uuid: string;
        dashboardId: string;
        changedOn: string;
      }>(
        `${this._apiConstantsService.supersetUrl}/dashboards/${sourceId}/embedded`
      )
      .pipe(map(res => res.uuid));
  }

  getOrganizationCharts(): Observable<SuperSetDashboard[]> {
    const { organizationChartIds } = this._constantsService.environment;

    return of([
      {
        id: organizationChartIds.one,
        title: 'Entreprises inscrites',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          organizationChartIds.one,
        thumbnailUrl: ''
      },
      {
        id: organizationChartIds.two,
        title: 'Entreprises actives',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          organizationChartIds.two,
        thumbnailUrl: ''
      },
      {
        id: organizationChartIds.three,
        title: "Entreprises en cours d'inscription",
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          organizationChartIds.three,
        thumbnailUrl: ''
      },
      {
        id: organizationChartIds.four,
        title: 'Actives en Cessation / Sommeil',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          organizationChartIds.four,
        thumbnailUrl: ''
      }
    ]);
  }

  getInvoiceCharts(): Observable<SuperSetDashboard[]> {
    const { invoiceChartIds } = this._constantsService.environment;

    return of([
      {
        id: invoiceChartIds.two,
        title: 'Factures de Vente',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          invoiceChartIds.two,
        thumbnailUrl: ''
      },
      {
        id: invoiceChartIds.three,
        title: "Factures d'Avoir",
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          invoiceChartIds.three,
        thumbnailUrl: ''
      },
      {
        id: invoiceChartIds.four,
        title: 'BAPA',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          invoiceChartIds.four,
        thumbnailUrl: ''
      },
      {
        id: invoiceChartIds.one,
        title: 'Factures Proforma',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          invoiceChartIds.one,
        thumbnailUrl: ''
      }
    ]);
  }

  getReceiptCharts(): Observable<SuperSetDashboard[]> {
    const { receiptChartIds } = this._constantsService.environment;

    return of([
      {
        id: receiptChartIds.one,
        title: 'Total Reçus',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          receiptChartIds.one,
        thumbnailUrl: ''
      },
      {
        id: receiptChartIds.two,
        title: 'Marchands Actifs',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          receiptChartIds.two,
        thumbnailUrl: ''
      },
      {
        id: receiptChartIds.three,
        title: 'TERNEs Activés',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          receiptChartIds.three,
        thumbnailUrl: ''
      },
      {
        id: receiptChartIds.four,
        title: 'Fournisseurs Agrées',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          receiptChartIds.four,
        thumbnailUrl: ''
      }
    ]);
  }

  getPaymentCharts(): Observable<SuperSetDashboard[]> {
    const { paymentChartIds } = this._constantsService.environment;

    return of([
      {
        id: paymentChartIds.one,
        title: 'Total transactions',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          paymentChartIds.one,
        thumbnailUrl: ''
      },
      {
        id: paymentChartIds.two,
        title: 'Transactions réussies',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          paymentChartIds.two,
        thumbnailUrl: ''
      },
      {
        id: paymentChartIds.three,
        title: 'Transactions échouées',
        url:
          'https://superset.dgi.fne.dev.veone.net/superset/explore/?standalone=3&slice_id=' +
          paymentChartIds.three,
        thumbnailUrl: ''
      }
    ]);
  }

  getCharts(
    pageName:
      | PageName.invoices
      | PageName.receiptList
      | PageName.organizationList
      | PageName.payments
  ): Observable<SuperSetDashboard[]> {
    let items: SuperSetDashboard[] = [];
    const {
      invoiceChartIds,
      receiptChartIds,
      organizationChartIds,
      paymentChartIds
    } = this._constantsService.environment;

    switch (pageName) {
      case PageName.invoices:
        return this.getInvoiceCharts();

      case PageName.receiptList:
        return this.getReceiptCharts();

      case PageName.organizationList:
        return this.getOrganizationCharts();

      case PageName.payments:
        return this.getPaymentCharts();
      default:
        return of([]);
    }

    // return this._statisticService.getSupersetResource('33').pipe(
    //   switchMap(data =>
    //     of({
    //       page: 1,
    //       itemPerPage: 10,
    //       itemOffset: 0,
    //       fullyItems: 0,
    //       pageNumber: 1,
    //       items,
    //       metadata: []
    //     })
    //   )
    // );
  }

  getSupersetChartData(
    dashboard: SuperSetDashboard,
    params?: SuperSetDashboardParams
  ): Observable<{
    data: SupersetResource;
    id: string;
    title: string;
    thumbnailUrl: string;
    url: string;
  }> {
    return this._statisticService
      .getSupersetResource(dashboard.id, params)
      .pipe(map(data => ({ ...dashboard, data })));
  }

  static pickPermittedDashboards(
    dashboards: SuperSetDashboard[],
    permissions: string[]
  ): SuperSetDashboard[] {
    return dashboards;
    // return dashboards.filter(el =>
    //   el.permissions
    //     ? el.permissions.every(permissionItem =>
    //         permissions.includes(permissionItem)
    //       )
    //     : true
    // );
  }
}
